import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2, Users, KeyRound, ClipboardList, LogOut, Search, Menu, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import SEOHead from '@/components/SEOHead';
import { useMediaQuery } from '@/hooks/useMediaQuery';

/**
 * Dedicated shell for AQA firms — aligned with DashboardLayoutV2 patterns.
 */
export default function AuditFirmShell({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.audit_membership?.role === 'admin';
  const firmName = user?.audit_membership?.firm_name || 'Audit Company';
  const isImpersonating = typeof window !== 'undefined'
    && !!localStorage.getItem('original_superadmin_token');
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((s) => s[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || (user?.email || '?')[0].toUpperCase();

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
      isActive
        ? 'bg-slate-900 text-white'
        : 'text-slate-700 hover:bg-slate-100'
    }`;

  const nav = (
    <>
      <div className="mb-4 px-2">
        <div className="flex items-center gap-2 text-slate-900 font-semibold">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Building2 className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <span className="block truncate">{firmName}</span>
            <p className="text-xs text-muted-foreground font-normal">Audit firm portal</p>
          </div>
        </div>
      </div>

      <nav className="flex flex-col gap-1 flex-1">
        {isAdmin && (
          <>
            <NavLink to="/audit-firm" end className={linkClass}>
              <ClipboardList className="h-4 w-4 shrink-0" /> Engagements
            </NavLink>
            <NavLink to="/audit-firm/redeem" className={linkClass}>
              <KeyRound className="h-4 w-4 shrink-0" /> Redeem code
            </NavLink>
            <NavLink to="/audit-firm/team" className={linkClass}>
              <Users className="h-4 w-4 shrink-0" /> Team
            </NavLink>
          </>
        )}

        <NavLink to="/auditor" className={linkClass}>
          <Search className="h-4 w-4 shrink-0" /> My audits
        </NavLink>
      </nav>

      <div className="mt-auto pt-4 border-t border-slate-200 space-y-2">
        <div className="flex items-center gap-2 px-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-slate-100 text-slate-700 text-xs">{initials}</AvatarFallback>
          </Avatar>
          <p className="text-xs text-muted-foreground truncate flex-1">{user?.email}</p>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start min-h-[44px]"
          onClick={() => {
            onLogout();
            navigate('/login');
          }}
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className={`min-h-screen bg-slate-50 flex flex-col ${isImpersonating ? 'pt-14' : ''}`}>
      <SEOHead
        pagePath={location.pathname}
        defaultTitle="Provisoft | Audit Portal"
        defaultDescription="Provisoft audit firm portal"
      />
      <ImpersonationBanner />

      {/* Mobile top bar */}
      <div className={`lg:hidden fixed left-0 right-0 h-14 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-3 ${isImpersonating ? 'top-14' : 'top-0'}`}>
        <button
          type="button"
          onClick={() => setMobileMenuOpen((o) => !o)}
          className="p-2 rounded-lg hover:bg-slate-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <X className="h-6 w-6 text-slate-600" /> : <Menu className="h-6 w-6 text-slate-600" />}
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-slate-700 shrink-0" />
          <span className="text-sm font-semibold truncate">{firmName}</span>
        </div>
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-slate-100 text-slate-700 text-xs">{initials}</AvatarFallback>
        </Avatar>
      </div>

      {mobileMenuOpen && isMobile && (
        <button
          type="button"
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          aria-label="Close navigation overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className={`flex flex-1 min-h-0 ${isImpersonating ? '' : ''}`}>
        <aside
          className={`
            fixed lg:static z-50 lg:z-auto top-0 bottom-0 left-0 w-[80%] max-w-xs lg:w-64
            border-r border-slate-200 bg-white p-4 flex flex-col gap-2
            transition-transform duration-300
            ${isImpersonating ? 'pt-[7.5rem] lg:pt-4' : 'pt-16 lg:pt-4'}
            ${mobileMenuOpen || !isMobile ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {nav}
        </aside>

        <main className={`flex-1 overflow-auto min-w-0 ${isMobile ? (isImpersonating ? 'pt-[7.5rem]' : 'pt-14') : ''}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
